import * as React from 'react';
import { cn } from '@/utils/cn';
import { IconInfoCircle, IconMinus, IconPlus } from '@tabler/icons-react';

export interface TeeInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  label?: string;
  hint?: string;
  inputSize?: 's' | 'm';
  status?: 'default' | 'filled' | 'focus' | 'disabled';
  destructive?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  showLeadingIcon?: boolean;
  showTrailingIcon?: boolean;
  showNumberControls?: boolean;
}

const TeeInput = React.forwardRef<HTMLInputElement, TeeInputProps>(
  (
    {
      className,
      label,
      hint,
      inputSize = 's',
      status = 'default',
      destructive = false,
      leadingIcon,
      trailingIcon,
      showLeadingIcon = true,
      showTrailingIcon = true,
      showNumberControls = false,
      disabled,
      type,
      value,
      onChange,
      min,
      max,
      ...props
    },
    ref
  ) => {
    const isNumberInput = type === 'number' && showNumberControls;

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === 'number') {
        const inputValue = e.target.value;
        // Allow empty string, minus sign, and valid numbers
        if (
          inputValue === '' ||
          inputValue === '-' ||
          !isNaN(Number(inputValue))
        ) {
          onChange?.(e);
        }
      } else {
        onChange?.(e);
      }
    };

    const handleIncrement = () => {
      const currentValue = Number(value) || 0;
      const newValue = currentValue + 1;
      const event = new Event('change', { bubbles: true });
      Object.defineProperty(event, 'target', {
        writable: false,
        value: { value: String(newValue) },
      });
      onChange?.(event as any);
    };

    const handleDecrement = () => {
      const currentValue = Number(value) || 0;
      const newValue = Math.max(0, currentValue - 1);
      const event = new Event('change', { bubbles: true });
      Object.defineProperty(event, 'target', {
        writable: false,
        value: { value: String(newValue) },
      });
      onChange?.(event as any);
    };
    const sizeClasses = {
      s: 'min-h-[40px] px-3 py-2',
      m: 'min-h-[48px] px-3 py-2',
    };

    const statusClasses = {
      default: 'border-[var(--border-default)] bg-[var(--background-card)]',
      filled: 'border-[var(--border-default)] bg-[var(--background-card)]',
      focus:
        'border-[hsl(var(--ring))] bg-[var(--background-card)] ring-1 ring-[hsl(var(--ring))] ring-offset-0',
      disabled:
        'border-[var(--border-default)] bg-[var(--background-input-field-disabled)] opacity-50 cursor-not-allowed',
    };

    const destructiveClasses = destructive
      ? 'border-red-500 focus-visible:ring-red-500'
      : '';

    const baseInputClasses = cn(
      'flex items-center rounded-[var(--spacing-xs)] border border-solid',
      "font-[family-name:var(--type-font-family-body,'Urbanist:regular',sans-serif)]",
      'font-[var(--type-font-weight-regular,normal)]',
      'text-[length:var(--font-size-body-s,14px)]',
      'leading-[var(--line-height-body-s,22px)]',
      'text-[var(--text-content-title)] placeholder:text-[var(--text-content-sub-title)]',
      'focus-visible:outline-none focus-within:ring-1 focus-within:ring-[hsl(var(--ring))] focus-within:border-[hsl(var(--ring))]',
      'transition-all duration-200',
      sizeClasses[inputSize],
      statusClasses[status],
      destructiveClasses,
      disabled && 'disabled:cursor-not-allowed disabled:opacity-50'
    );

    const inputClasses = cn(baseInputClasses, 'w-full gap-2', className);

    const numberInputClasses = cn(
      baseInputClasses,
      'w-full gap-1 px-2 py-2 overflow-hidden'
    );

    const buttonClasses =
      'flex items-center justify-center text-[var(--text-content-sub-title)] hover:text-[var(--text-content-title)] cursor-pointer transition-colors flex-shrink-0';

    const labelClasses = cn(
      'text-body-s',
      'font-medium',
      'text-[var(--text-label-title)]',
      destructive && 'text-red-500'
    );

    const hintClasses = cn(
      'text-body-xs',
      'font-medium',
      'text-[var(--text-label-sub-label)]',
      destructive && 'text-red-500'
    );

    return (
      <>
        {isNumberInput ? (
          <div className="flex w-full flex-col gap-1.5">
            {label && <label className={labelClasses}>{label}</label>}
            <div className={numberInputClasses}>
              <button
                type="button"
                onClick={handleDecrement}
                disabled={disabled}
                className={buttonClasses}
                aria-label="Decrease value"
              >
                <IconMinus size={16} />
              </button>

              {showLeadingIcon && leadingIcon && (
                <div className="flex shrink-0 items-center">{leadingIcon}</div>
              )}

              <input
                ref={ref}
                disabled={disabled}
                type={type}
                value={value}
                onChange={type === 'number' ? handleNumberChange : onChange}
                className="flex-1 min-w-0 bg-transparent outline-none text-center"
                {...(type === 'number' ? { step: 'any' } : {})}
                {...(type === 'number' ? {} : { min, max })}
                {...props}
              />

              {showTrailingIcon && trailingIcon && (
                <div className="flex shrink-0 items-center">{trailingIcon}</div>
              )}

              <button
                type="button"
                onClick={handleIncrement}
                disabled={disabled}
                className={buttonClasses}
                aria-label="Increase value"
              >
                <IconPlus size={16} />
              </button>
            </div>
            {hint && (
              <div className="flex gap-1 items-start">
                <IconInfoCircle size={16} className={hintClasses} />
                <p className={hintClasses}>{hint}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex w-full min-w-0 flex-col gap-1.5">
            {label && <label className={labelClasses}>{label}</label>}

            <div className={inputClasses}>
              {showLeadingIcon && leadingIcon && (
                <div className="flex shrink-0 items-center">{leadingIcon}</div>
              )}

              <input
                ref={ref}
                disabled={disabled}
                type={type}
                value={value}
                onChange={type === 'number' ? handleNumberChange : onChange}
                className={cn(
                  'flex-1 min-w-0 bg-transparent outline-none',
                  type === 'number' && '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                )}
                {...(type === 'number' ? { step: 'any' } : {})}
                {...(type === 'number' ? {} : { min, max })}
                {...props}
              />

              {showTrailingIcon && trailingIcon && (
                <div className="flex shrink-0 items-center">{trailingIcon}</div>
              )}
            </div>

            {hint && (
              <div className="flex gap-1 items-start">
                <IconInfoCircle size={16} className={hintClasses} />
                <p className={hintClasses}>{hint}</p>
              </div>
            )}
          </div>
        )}
      </>
    );
  }
);

TeeInput.displayName = 'TeeInput';

export { TeeInput };
export type { TeeInputProps as TeeInputPropsType };
